import React, { useState, useEffect } from "react";
import { Heading, ButtonSolid } from "../../components/styledConstants";
import MaterialInput from "../../components/common/UI/MaterialInput";
import iconattachment from "../../assets/images/attachment.png";
import { Wrapper } from "./style";
import ErrorMessage from "../../components/ErrorMessage";
import SuccessMessage from "../../components/SuccessMessage";
import Request from "../../utils/Request";
import config from "../../utils/config";
import { useParams } from "react-router-dom";

export default function Create() {
  const formField = {
    title: "",
    body: "",
    image: "",
  };

  const [formData, updateFormData] = useState({ ...formField });
  const [formErrors, setFormErrors] = useState({});
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedImage, setSelectedImage] = useState("");
  const [imageFile, setImageFile] = useState("");
  const [pageTitle, setPageTitle] = useState("Upload Notification Banner");
  const [buttonTitle, setButtonTitle] = useState("Upload Banner");
  const { bannerId } = useParams();

  useEffect(() => {
    if (bannerId) {
      getBanner(bannerId);
      setPageTitle("Update Notification Banner");
      setButtonTitle("Update Banner");
    } else {
      updateFormData({ ...formField });
    }
  }, [bannerId]);

  const getBanner = (bannerid) => {
    const successHandler = (res) => {
      if (res) {
        if (res.data && res.data.content) {
          let formvalue = formData;
          formvalue.title = res.data.content[0].title;
          formvalue.body = res.data.content[0].body;
          formvalue.image = res.data.content[0].image;

          setSelectedImage(res.data.content[0].image);

          updateFormData({ ...formvalue });
        }
      }
    };
    const errorHandler = () => {};
    const api = new Request("", successHandler, errorHandler, false);
    let payload = {};
    payload.pagination = {
      pageNo: 1,
      pageSize: 100,
    };
    payload.title = "";
    payload.bannerId = bannerid;
    return api.post(`${config.apis.admin.BANNER_LIST}`, payload);
  };

  const handleFormDataChange = (event) => {
    updateFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
    setFormErrors({ ...formErrors, [event.target.name]: "" });
  };


  const handleFileUpload = (value, name, tempfile) => {
    if (!tempfile) {
      setSelectedImage("");
      setImageFile("");
      updateFormData({ ...formData, image: "" });
      return;
    }

    setImageFile(tempfile);

    // Use FileReader to get a base64 Data URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setSelectedImage(reader.result); // base64 data URL
    };
    reader.readAsDataURL(tempfile);

    updateFormData({
      ...formData,
      [name]: tempfile.name,
    });

    setFormErrors({ ...formErrors, [name]: "" });
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();
    setMessage("");
    setErrorMessage("");

    // Basic validation
    const errors = {};

    if (!formData.title.trim()) errors.title = "Title is required";
    if (!formData.body.trim()) errors.body = "Description is required";
    if (!selectedImage) errors.image = "Image is required";

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    if (bannerId) {
      updateBanner();
    } else {
      addBanner();
    }
  };

  const addBanner = () => {
    const successHandler = (res) => {
      if (res?.code === "0000") {
        setMessage("Banner created successfully!");
        updateFormData({ ...formField });
        setSelectedImage("");
        setImageFile("");
      } else {
        setErrorMessage(res?.msg || "Failed to create banner");
      }
    };

    const errorHandler = () => {
      setErrorMessage("Something went wrong. Please try again.");
    };

    const api = new Request("", successHandler, errorHandler, false);

    const payload = new FormData();

    // ✅ Wrap JSON in a Blob so it's sent as application/json
    const bannerJson = new Blob(
      [JSON.stringify({ title: formData.title, body: formData.body })],
      { type: "application/json" }
    );
    payload.append("banner", bannerJson);

    // ✅ Append image file normally
    payload.append("image", imageFile);

    return api.post(`${config.apis.admin.ADD_BANNER}`, payload);
  };

  const updateBanner = () => {
    const successHandler = (res) => {
      if (res?.code === "0000") {
        setMessage("Banner updated successfully!");
      } else {
        setErrorMessage(res?.msg || "Failed to update banner");
      }
    };

    const errorHandler = () => {
      setErrorMessage("Something went wrong. Please try again.");
    };

    const api = new Request("", successHandler, errorHandler, false);

    const payload = new FormData();

    // ✅ Wrap JSON in a Blob so it's sent as application/json
    const bannerJson = new Blob(
      [JSON.stringify({ id: bannerId, title: formData.title, body: formData.body })],
      { type: "application/json" }
    );
    payload.append("banner", bannerJson);

    // ✅ Append image file if a new one is selected
    if (imageFile) {
      payload.append("image", imageFile);
    }

    return api.post(`${config.apis.admin.BANNER_UPDATE}`, payload);
  };

  return (
    <Wrapper>
      <div className="flex flex-center space-between mb10">
        <Heading size="xxxl">{pageTitle}</Heading>
      </div>

      <form className="mt32" onSubmit={handleFormSubmit}>
        <div className="form-wrapper">
          {message && <><SuccessMessage successMessage={message} /><br /></>}
          {errorMessage && <><ErrorMessage error={errorMessage} /><br /></>}

          <div className="form-inner">
            {bannerId && (
              <div className="flex flex-center mt10 mb20">
                <MaterialInput
                  type="text"
                  name="bannerId"
                  value={bannerId}
                  placeholder="Banner Id"
                  disabled={true}
                />
              </div>
            )}
            <div className="flex flex-center mt10 gap28 mb20">
              <MaterialInput
                name="title"
                value={formData.title}
                onChange={handleFormDataChange}
                placeholder="Title"
                type="text"
                error={formErrors.title}
              />
            </div>

            <div className="flex flex-center mt10 gap28 mb20">
              <MaterialInput
                name="body"
                value={formData.body}
                onChange={handleFormDataChange}
                placeholder="Description"
                type="text"
                error={formErrors.body}
              />
            </div>

            <div className="flex flex-center mt10 mb20 wd50">
              <MaterialInput
                icon={
                  <span className="icon-attachment">
                    <img src={iconattachment} alt="attachment" />
                  </span>
                }
                accept=".png, .jpeg, .jpg"
                name="image"
                onChange={handleFileUpload}
                value={formData.image}
                type="file"
                placeholder="Image"
                error={formErrors.image}
                uploadUrl="/user-service/upload/file"
              />
            </div>

            {selectedImage && (
              <div className="flex flex-center mt10 mb20 wd50">
                <img height="70" src={selectedImage} alt="preview" />
              </div>
            )}

            <ButtonSolid width="320px" primary xl className="mt20">
              {buttonTitle}
            </ButtonSolid>
          </div>
        </div>
      </form>
    </Wrapper>
  );
}